/*
 * Sliding number, ported from Animate UI (https://animate-ui.com), MIT + Commons Clause,
 * Copyright (c) 2025 Elliot Sutton. See THIRD_PARTY_NOTICES.md.
 * Simplified for this site: each digit rolls to the new value when `value` changes.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { motionAllowed } from "./motionEnv";

const SPRING = { stiffness: 200, damping: 20, mass: 0.4 };

function Digit({ motionValue, number, height }) {
  const y = useTransform(motionValue, (latest) => {
    if (!height) return 0;
    const current = latest % 10;
    const offset = (10 + number - current) % 10;
    let t = offset * height;
    if (offset > 5) t -= 10 * height;
    return t;
  });
  return (
    <motion.span style={{ y, position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {number}
    </motion.span>
  );
}

function Roller({ value, place }) {
  const target = Math.floor(value / place) % 10;
  const mv = useSpring(target, SPRING);
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => { if (ref.current) setHeight(ref.current.getBoundingClientRect().height); }, []);
  useEffect(() => { mv.set(target); }, [target, mv]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", width: "1ch", overflowY: "clip", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
      <span style={{ visibility: "hidden" }}>0</span>
      {height > 0 && Array.from({ length: 10 }, (_, i) => <Digit key={i} motionValue={mv} number={i} height={height} />)}
    </span>
  );
}

/** Indian digit grouping (1,754 / 12,345 / 1,23,456). */
function groups(len) {
  const marks = new Set();
  for (let right = 3; right < len; right += 2) marks.add(len - right);
  return marks;
}

export default function SlidingNumber({ value, className = "", prefix = "", ...rest }) {
  const n = Math.max(0, Math.round(Number(value) || 0));
  const str = String(n);
  if (!motionAllowed()) {
    return <span className={className} {...rest}>{prefix}{n.toLocaleString("en-IN")}</span>;
  }
  const marks = groups(str.length);
  return (
    <span className={className} aria-label={`${prefix}${n.toLocaleString("en-IN")}`} {...rest}>
      <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
        {prefix}
        {str.split("").map((_, i) => (
          <span key={`${str.length}-${i}`} style={{ display: "inline-flex" }}>
            {marks.has(i) && <span>,</span>}
            <Roller value={n} place={10 ** (str.length - i - 1)} />
          </span>
        ))}
      </span>
    </span>
  );
}
